// Libraries
import React, {
  useCallback,
  useEffect,
  useContext,
  useState,
  useRef,
} from 'react';
import { useDropzone } from 'react-dropzone';
import { Accordion, Alert, Card } from 'react-bootstrap';
import { ExclamationCircle, ChevronDown } from 'react-bootstrap-icons';
import { StickyTable, Row, Cell } from 'react-sticky-table';
import moment from 'moment';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

// Relative imports
import './stepone_file_upload.component.css';
import AppContext from '../../../../context';
import { parseTransactions } from '../../../../../api';
import TxTable from '../tx_table.component';
import { Loader, Number } from '../../../common/common';
import { hdfc, zerodha } from './template_parsers';

const StepOneFileUpload = (props) => {
  const [acceptedFiles, setAcceptedFiles] = useState([]);
  const onDrop = useCallback((accFiles) => {
    setAcceptedFiles(accFiles);
    if (accFiles.length) {
      parseCSV(accFiles[0]);
      setIsLoaderVisible(true);
    }
  });
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  // Context
  const context = useContext(AppContext);
  const portfolio_id = props.pfId;
  // Upload
  const [parsedTxs, setParsedTxs] = useState([]);
  const [parseErrors, setParseErrors] = useState([]);
  const [isLoaderVisible, setIsLoaderVisible] = useState(false);
  const [template, setTemplate] = useState('tijori');
  const parseErrorsRef = useRef(null);
  const tableContRef = useRef(null);
  const [isErrorsGuideVisible, setIsErrorsGuideVisible] = useState(false);
  const [tableContHeight, setTableContHeight] = useState('');
  // Form errors
  const [isError, setIsError] = useState(false);
  const [errorText, setErrorText] = useState('');

  const parsers = {
    hdfc: hdfc,
    tijori: null,
    zerodha: zerodha,
  };

  const needsFuzzySeach = () => {
    const fuzzyTemplates = ['hdfc'];
    return fuzzyTemplates.includes(template);
  };

  const parseCSV = (fileObj) => {
    const reader = new FileReader();
    reader.onload = function (evt) {
      const fileArrBuffer = evt.target.result;
      const theExcel = XLSX.read(fileArrBuffer, {
        type: 'array',
        cellDates: true,
        cellText: false,
      });
      const sheetName = theExcel.SheetNames[0];
      const theSheet = theExcel.Sheets[sheetName];
      const csvString = XLSX.utils.sheet_to_csv(theSheet, {
        blankrows: false,
        strip: true,
        dateNF: 'dd"-"mm"-"yyyy',
      });
      Papa.parse(csvString, {
        complete: (results) => {
          let transactions = results.data.splice(1);
          const parser = parsers[template];
          if (parser !== null) {
            transactions = parser.parse(transactions);
          }
          setParsedTxs(transactions);
        },
        skipEmptyLines: true,
      });
    };
    reader.readAsArrayBuffer(fileObj);
  };

  useEffect(() => {
    if (parsedTxs.length === 0) {
      setIsLoaderVisible(false);
      return;
    }
    const parseTxs = async () => {
      const _txs = parsedTxs.map((tx) => ({
        company_name: tx[0],
        primary_identifier: tx[1],
        transaction_type: tx[2],
        number_of_shares: tx[3],
        tx_date: tx[4],
        share_price: tx[5],
        amount: tx[6],
        secondary_identifier: tx[7],
        trade_id: tx[8],
        order_id: tx[9],
      }));
      try {
        let offset = 2;
        const parser = parsers[template];
        if (parser !== null) {
          offset = parser.offset;
        }
        const res = await parseTransactions(
          portfolio_id,
          {
            transactions: _txs,
            offset: offset,
            format: template,
          },
          needsFuzzySeach(),
        );
        const valid = res.data && res.data.valid;
        const invalid = res.data && res.data.invalid;

        const validTxs = valid.map((t) => {
          return {
            company_id: t['company_id'],
            company_name: t['company_name'],
            type: t['transaction_type'],
            shares: t['number_of_shares'],
            date: t['tx_date'],
            date_obj: moment(t['tx_date'], 'DD-MM-YYYY').toDate(),
            price: t['share_price'],
            cost_value: t['amount'],
            data_inception_date: t['data_inception_date'],
            fincode: t['fincode'],
            trade_id: t['trade_id'] || null,
            order_id: t['order_id'] || null,
            scrip: t['primary_identifier'],
          };
        });
        const invalidTxs = invalid.map((t) => {
          return {
            error: t['error'],
            row_no: t['row_no'],
          };
        });
        props.setTxs(validTxs);
        const txsFincode = valid.map((t) => t.fincode);
        props.setTxsFincode(txsFincode);
        setParseErrors(invalidTxs);
        setIsLoaderVisible(false);
        props.setParsedTxsLength({
          ...props.setParsedTxsLength,
          valid: valid.length,
          invalid: invalid.length,
        });
      } catch (error) {
        console.log(error);
      }
    };
    parseTxs();
  }, [parsedTxs]);

  useEffect(() => {
    props.setFileUploadTemplate(template);
    if (props.parsedTxsLength.valid === 0) {
      props.setParsedTxsLength({});
    }
  }, [template]);

  useEffect(() => {
    if (!tableContHeight) {
      const modalContainer = props.containerRef.current.parentElement;
      const modalContainerHeight =
        modalContainer.getBoundingClientRect().height;
      const modalContainerPad = window.getComputedStyle(modalContainer);
      const modalContainerPaddingTop = parseInt(
        modalContainerPad.getPropertyValue('padding-top'),
      );
      const modalContainerPaddingBottom = parseInt(
        modalContainerPad.getPropertyValue('padding-bottom'),
      );
      const headerHeight =
        props.containerRef.current.firstChild.getBoundingClientRect().height;
      const tableContHeight =
        modalContainerHeight -
        headerHeight -
        (modalContainerPaddingTop + modalContainerPaddingBottom) -
        30;
      setTableContHeight(tableContHeight);
    }
  }, []);

  useEffect(() => {
    if (props.txs.length) {
      if (tableContRef.current) {
        setTimeout(() => {
          const tableRows = tableContRef.current.children[0].children[0];
          const parseErrorsCont = tableRows.lastChild;
          const tableContRect = tableContRef.current.getBoundingClientRect();
          const tableContBottom = tableContRect.bottom;
          let isVisible = false;
          tableContRef.current.children[0].addEventListener('scroll', () => {
            const top = parseErrorsCont.getBoundingClientRect().top;
            if (top < tableContBottom && !isVisible) {
              setIsErrorsGuideVisible(true);
              isVisible = true;
              return;
            }
            if (top >= tableContBottom && isVisible) {
              setIsErrorsGuideVisible(false);
              isVisible = false;
              return;
            }
          });
        }, 0);
      }
    }
  }, [props.txs]);

  const templateLink =
    'https://tijori-excel.s3.ap-south-1.amazonaws.com/portfolio/excel/Upload_Template.xlsx';
  const zerodhaGuideLink =
    'https://drive.google.com/file/d/1M2_oSu_VM_0Va7UjvivbC8GEZWXIaj8m/';
  const hdfcGuideLink =
    'https://drive.google.com/file/d/1tRPgPyCiTbuC8f1daiRm_EJzXo7vaZGt/';
  const companiesMapLink =
    'https://docs.google.com/spreadsheets/d/19CwuvowteZ3Pp8rhHax1IvX39BITgCda/export?format=xlsx';

  const getOffer = (tx) => {
    if (tx.type === 'BONUS' || tx.type === 'SPLIT') {
      return `${tx.ratio_1}:${tx.ratio_2}`;
    } else if (tx.type === 'DIVIDEND') {
      return `₹${tx.ratio_3} per share`;
    } else {
      return '-';
    }
  };

  return (
    <>
      <section className="w-100">
        {props.txs.length > 0 && props.invalidComps.length > 0 && (
          <div className="text-danger sell-tx-error file-upload">
            {props.invalidComps.length === 1
              ? 'Invalid sell transaction: You do not have enough shares.'
              : 'Invalid sell transactions: You do not have enough shares.'}
          </div>
        )}
        {isError && errorText && (
          <span className="text-danger sell-tx-error file-upload">
            {errorText}
          </span>
        )}
        {props.txs.length > 0 && props.parsedTxsLength.valid ? (
          <div className="txs-status">
            {props.parsedTxsLength.invalid ? (
              <div>
                {`${props.parsedTxsLength.valid} of ${
                  props.parsedTxsLength.valid + props.parsedTxsLength.invalid
                } transactions are valid.  `}{' '}
                <ExclamationCircle className="invalid-txs-icon" />
                <span
                  className="invalid-txs"
                  onClick={() => {
                    parseErrorsRef.current.scrollIntoView();
                  }}
                >
                  {props.parsedTxsLength.invalid}{' '}
                  {props.parsedTxsLength.invalid === 1
                    ? 'invalid transaction'
                    : 'invalid transactions'}
                </span>
                .
              </div>
            ) : (
              <div></div>
            )}
          </div>
        ) : null}
      </section>
      <section className="h-100">
        {props.txs.length > 0 && !props.isManual ? (
          !props.showErroredBsdTxs ? (
            <>
              <div
                style={{ height: tableContHeight, overflowY: 'auto' }}
                className="position-relative"
                ref={tableContRef}
              >
                {props.isLoaderVisible ? (
                  <div className="overlay-loader-cont">
                    <Loader />
                  </div>
                ) : null}
                <StickyTable
                  className="add-tx-table table-responsive"
                  stickyFooterCount={0}
                  leftStickyColumnCount={0}
                >
                  <Row className="sticky-table-thead">
                    <Cell className="pl-3 pr-3 search-width">COMPANY NAME</Cell>
                    <Cell className="pl-3 pr-3">TRANSACTION TYPE</Cell>
                    <Cell className="text-right pl-3 pr-5">SHARES</Cell>
                    <Cell className="pl-3 pr-3">DATE</Cell>
                    <Cell className="text-right pl-3 pr-3">SHARE PRICE</Cell>
                    <Cell className="text-right pl-3 pr-3">AMOUNT</Cell>
                    <Cell className="text-right pl-3 pr-3">{'   '}</Cell>
                  </Row>
                  <TxTable
                    setTxs={props.setTxs}
                    txs={props.txs}
                    txsFincode={props.txsFincode}
                    setTxsFincode={props.setTxsFincode}
                    removeTx={props.removeTx}
                    containerRef={props.containerRef}
                    invalidComps={props.invalidComps}
                    isError={isError}
                    setIsError={setIsError}
                    errorText={errorText}
                    setErrorText={setErrorText}
                  ></TxTable>

                  {parseErrors && parseErrors.length > 0 && (
                    <section
                      ref={parseErrorsRef}
                      className="position-absolute w-100 errors"
                    >
                      <Alert variant={'danger'}>
                        {parseErrors.map((err, i) => {
                          if (parseErrors.length - 1 === i) {
                            return (
                              <div key={i}>
                                Row {err['row_no']}: {err['error']}
                              </div>
                            );
                          } else {
                            return (
                              <div key={i}>
                                <div>
                                  Row {err['row_no']}: {err['error']}
                                </div>
                                <hr style={{ margin: '0.5em 0' }} />
                              </div>
                            );
                          }
                        })}
                      </Alert>
                    </section>
                  )}
                </StickyTable>
              </div>
              {isErrorsGuideVisible ? (
                <div>
                  For the invalid transactions, kindly update the information
                  with the correct company name & symbol from the mapping file
                  available{' '}
                  <a
                    href={companiesMapLink}
                    target="_blank"
                    className="template-link"
                  >
                    here
                  </a>
                </div>
              ) : null}
            </>
          ) : (
            <div>
              <div>
                Transactions of these companies results in negative no. of
                shares. You can resolve by uploading all historic transactions
                for these companies.
              </div>
              {props.erroredBsdTxs.map((ctxs, idx_comp) => (
                <Accordion
                  className="company-accordion mt-2"
                  key={'acc-' + idx_comp}
                >
                  <Card>
                    <Accordion.Toggle
                      as={Card.Header}
                      eventKey={'c-' + idx_comp}
                      className="no-bg"
                    >
                      <b>{ctxs.company.name}</b>
                      <span className="pull-right">
                        <span className="pr-3">
                          Balance shares:{' '}
                          <span style={{ fontWeight: 500 }}>
                            {ctxs.total_shares}
                          </span>
                        </span>
                        <span className="pull-right">
                          <ChevronDown />
                        </span>
                      </span>
                    </Accordion.Toggle>
                    <Accordion.Collapse eventKey={'c-' + idx_comp}>
                      <Card.Body className="accordion-body">
                        <table className="view-tx-table table-responsive">
                          <tbody className="table-body">
                            <tr>
                              <th className="pl-3 pr-3">Transaction Date</th>
                              <th className="pl-3 pr-3">Type</th>
                              <th className="pl-3 pr-3 text-right">Shares</th>
                              <th className="pl-3 pr-3 text-right">Price</th>
                              <th className="pl-3 pr-3 text-right">Offer</th>
                              <th className="pl-3 pr-3 text-right">Amount</th>
                              <th className="pl-3 pr-3 text-right">
                                Balance Shares
                              </th>
                            </tr>
                            {ctxs.transactions &&
                              [...ctxs.transactions]
                                .reverse()
                                .map((tx, idx_tx) => (
                                  <tr className="table-row" key={'t-' + idx_tx}>
                                    <td className="px-3 py-2">{tx.date}</td>
                                    <td className="px-3 py-2 text-capitalize">
                                      {tx.type.toLowerCase()}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                      {tx.shares}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                      {tx.price}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                      {getOffer(tx)}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                      <Number
                                        value={tx.amount}
                                        usePrefix={true}
                                      />
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                      {tx.balance}
                                    </td>
                                  </tr>
                                ))}
                          </tbody>
                        </table>
                      </Card.Body>
                    </Accordion.Collapse>
                  </Card>
                </Accordion>
              ))}
            </div>
          )
        ) : (
          <>
            <section className="mb-2">
              <span className="fs-label">File source: </span>
              <ul className="file-sources">
                <li>
                  <input
                    type="radio"
                    id="zerodha"
                    name="file_source"
                    value="zerodha"
                    checked={template === 'zerodha'}
                    onChange={() => setTemplate('zerodha')}
                  />
                    <label htmlFor="zerodha">Zerodha</label>
                </li>
                {/* <li>
                  <input
                    type="radio"
                    id="hdfc"
                    name="file_source"
                    value="hdfc"
                    checked={template === 'hdfc'}
                    onChange={() => setTemplate('hdfc')}
                  />
                    <label htmlFor="hdfc">HDFC</label>
                </li> */}
                <li>
                  <input
                    type="radio"
                    id="default"
                    name="file_source"
                    value="default"
                    checked={template === 'tijori'}
                    onChange={() => setTemplate('tijori')}
                  />
                    <label htmlFor="default">All Other Brokers</label>
                </li>
              </ul>
            </section>
            {template === 'tijori' && (
              <div className="mb-2">
                Download a{' '}
                <a
                  href={templateLink}
                  target="_blank"
                  className="template-link"
                >
                  sample CSV template
                </a>{' '}
                to see an example of the format required
              </div>
            )}
            {template === 'zerodha' && (
              <div className="mb-2">
                Upload the Tradebook report (xlsx) from your Zerodha account,{' '}
                <a
                  href={zerodhaGuideLink}
                  target="_blank"
                  className="template-link"
                >
                  Click here
                </a>{' '}
                for a detailed guide on where to get the report
              </div>
            )}
            {template === 'hdfc' && (
              <div className="mb-2">
                Upload the Trade Details report from your HDFC Sec account,{' '}
                <a
                  href={hdfcGuideLink}
                  target="_blank"
                  className="template-link"
                >
                  Click here
                </a>{' '}
                for a detailed guide on where to get the report
              </div>
            )}
            <div
              {...getRootProps({
                className:
                  'dropzone d-flex justify-content-center align-items-center',
              })}
            >
              {isLoaderVisible ? (
                <Loader />
              ) : (
                <>
                  <input {...getInputProps()} />
                  <div className="dropzone-content">
                    <div className="upload-btn">Add file</div>
                    <p className="mt-3">or drop your file to upload</p>
                  </div>
                </>
              )}
            </div>
            <p
              className={`text-danger ${
                props.parsedTxsLength.valid === 0 ? 'd-block' : 'd-none'
              }`}
            >
              Unable to read the uploaded file. Please make sure the file
              matches our specifications
            </p>
          </>
        )}
      </section>
    </>
  );
};

export default StepOneFileUpload;
